import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DialogRef } from '@angular/cdk/dialog';
import { firstValueFrom } from 'rxjs';
import {
  PaginatedResult,
  SelectableTechnician,
  SelectableTemplate,
  TechnicianStockLevelDraft,
  TechnicianStockLevelSelection,
  TechnicianStockLevelService,
} from '../../_services/technician-stock-level.service';
import { CustomerService, ProjectService } from '../../_services';
import { GERMAN_STATE_OPTIONS, germanStateCode, germanStateLabel } from '../../_helpers/german-states';

/** Ein auswählbarer Eintrag — Artikelvorlage oder Techniker, je nach Modus. */
interface Auswahl {
  id: number;
  label: string;
  /** Nur im Techniker-Modus: Bundesland, nach dem Schritt 2 gruppiert. */
  addressState?: string | null;
  alreadyDefined: boolean;
}

/** Eingetragene Mengen je Auswahl-ID. */
interface Mengen {
  min: number;
  target: number;
}

/**
 * Zweistufiger Dialog zum Anlegen von Mindest-/Sollbeständen.
 *
 * Dieselbe StockRow lässt sich von zwei Seiten pflegen — von der Technikerseite
 * ("welche Vorlagen führt dieser Techniker") und von der Vorlagenseite ("welche
 * Techniker führen diesen Artikel"). Der Dialog bildet beide Richtungen ab,
 * weil sie sich nur darin unterscheiden, was ausgewählt wird und was feststeht.
 *
 * Schritt 1 wählt die Gegenstücke aus, Schritt 2 trägt die Mengen ein. Bereits
 * definierte Gegenstücke erscheinen ausgegraut statt zu fehlen: Sonst sucht das
 * Büro nach etwas, das scheinbar nicht existiert.
 */
@Component({
  selector: 'hawk-technician-stock-level-dialog',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './technician-stock-level-dialog.component.html',
})
export class TechnicianStockLevelDialogComponent {
  private service = inject(TechnicianStockLevelService);
  private customerService = inject(CustomerService);
  private projectService = inject(ProjectService);
  private dialogRef = inject<DialogRef<TechnicianStockLevelSelection[]>>(DialogRef);

  /** 'technician' = Vorlagen für einen Techniker, 'template' = Techniker für eine Vorlage. */
  public mode = input.required<'technician' | 'template'>();
  public userId = input<number | null>(null);
  public articleTemplateId = input<number | null>(null);
  /** Bundesland des Technikers — Vorauswahl für die Durchschnittswerte. */
  public addressState = input<string | null>(null);
  /** Kunde der Vorlage — Vorfilter der Vorlagen-Auswahl. */
  public customerId = input<number | null>(null);
  /** Projekte, auf deren Projekttechniker die Auswahl vorgefiltert wird. */
  public projectIds = input<number[]>([]);
  /**
   * Kunden- und Projektzuordnung der Vorlage als Rohwerte (comment3/comment2).
   * Nur beim Anlegen relevant: Dort gibt es noch keinen Datensatz, aus dem das
   * Backend die Vorauswahl ableiten könnte.
   */
  public comment2 = input<string | null>(null);
  public comment3 = input<string | null>(null);
  /**
   * Bereits vergebene Gegenstücke, die das Backend noch nicht kennt — im
   * Anlegen-Dialog eines neuen Technikers gibt es die Zeilen erst lokal.
   */
  public pendingIds = input<number[]>([]);

  protected readonly stateOptions = GERMAN_STATE_OPTIONS;
  protected readonly stateLabel = germanStateLabel;

  protected step = signal<1 | 2>(1);
  protected loading = signal(false);
  protected fehler = signal<string | null>(null);

  // --- Schritt 1: Auswahl ---
  protected searchTerm = signal('');
  protected seite = signal(1);
  protected seitenAnzahl = signal(1);
  protected totalCount = signal(0);
  protected matches = signal<Auswahl[]>([]);
  protected kundenFilter = signal<number | null>(null);
  protected kunden = signal<{ id: number; name: string }[]>([]);
  /** Projektfilter im Vorlagen-Modus: Auswahl und verfügbare Projekte. */
  protected projekte = signal<{ id: number; name: string }[]>([]);
  protected projektFilter = signal<number[]>([]);
  /** true, solange die serverseitige Vorauswahl noch nicht übernommen wurde. */
  private projektFilterOffen = true;
  protected selected = signal<Auswahl[]>([]);

  // --- Schritt 2: Mengen ---
  /** Bundesland, über das die Durchschnittswerte gebildet werden. */
  protected durchschnittsLand = signal<string | null>(null);
  protected quantities = signal<Record<number, Mengen>>({});

  protected istTechnikerModus = computed(() => this.mode() === 'technician');

  protected titel = computed(() =>
    this.istTechnikerModus() ? 'Artikelvorlagen für diesen Techniker' : 'Techniker für diese Artikelvorlage'
  );

  protected selectedIds = computed(() => new Set(this.selected().map(e => e.id)));

  /** Alle Treffer der aktuellen Seite, die noch auswählbar sind. */
  protected selectableMatches = computed(() => this.matches().filter(e => !e.alreadyDefined));

  protected allFilteredSelected = computed(() => {
    const selectable = this.selectableMatches();
    if (selectable.length === 0) return false;
    const chosenIds = this.selectedIds();
    return selectable.every(e => chosenIds.has(e.id));
  });

  /**
   * Gewählte Projekte zuerst, danach alphabetisch.
   *
   * Die Liste ist scrollbar; ohne diese Sortierung kann ein gewähltes Projekt
   * ausserhalb des sichtbaren Bereichs liegen, und die Trefferliste ist enger,
   * als der Lagerist es sich erklären kann.
   */
  protected sortierteProjekte = computed(() => {
    const chosenIds = new Set(this.projektFilter());
    return [...this.projekte()].sort((a, b) => {
      const diff = Number(chosenIds.has(b.id)) - Number(chosenIds.has(a.id));
      return diff !== 0 ? diff : a.name.localeCompare(b.name, 'de');
    });
  });

  /** Im Techniker-Modus eine Gruppe, im Vorlagen-Modus je Bundesland eine. */
  protected groups = computed<{ key: string; label: string; entries: Auswahl[] }[]>(() => {
    const entries = this.selected();
    if (this.istTechnikerModus()) {
      return [{ key: 'alle', label: '', entries }];
    }

    const nachLand = new Map<string, Auswahl[]>();
    for (const entry of entries) {
      const key = entry.addressState || '';
      if (!nachLand.has(key)) nachLand.set(key, []);
      nachLand.get(key)!.push(entry);
    }
    return [...nachLand.entries()]
      .map(([key, group]) => ({ key, label: key ? germanStateLabel(key) : 'Ohne Bundesland', entries: group }))
      .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  });

  /**
   * 0 ist bewusst ungültig: Wer eine Vorlage nicht führen soll, bekommt keine
   * StockRow. Und der Sollbestand muss über dem Mindestbestand liegen, sonst hätte
   * das Auffüllen kein Ziel.
   */
  protected rowValid(id: number): boolean {
    const werte = this.quantities()[id];
    if (!werte) return false;
    return Number.isInteger(werte.min) && Number.isInteger(werte.target) &&
      werte.min > 0 && werte.target > werte.min;
  }

  protected allValid = computed(() =>
    this.selected().length > 0 && this.selected().every(e => this.rowValid(e.id))
  );

  /**
   * Der ModalService erzeugt die Komponente und setzt ihre Inputs erst DANACH
   * (componentRef.setInput) — beides im selben synchronen Block. In ngOnInit
   * stehen die Inputs deshalb noch auf ihren Vorgabewerten; ein Effect hilft
   * nicht, weil sein erster Lauf ebenfalls in diesen Block fällt.
   *
   * Der Microtask läuft, sobald dieser Block abgearbeitet ist. Erst dann sind
   * Bundesland, Kundenfilter und Projektvorgabe da.
   */
  public ngOnInit(): void {
    queueMicrotask(() => void this.initialisieren());
  }

  private async initialisieren(): Promise<void> {
    this.kundenFilter.set(this.customerId());
    // Auf den ISO-Code normalisieren: Altzeilen tragen den Klarnamen, der keine
    // Option der Auswahlliste treffen würde.
    this.durchschnittsLand.set(germanStateCode(this.addressState()));
    this.projektFilter.set(this.projectIds());
    this.projektFilterOffen = this.projectIds().length === 0;
    await this.erstesLaden();
  }

  private async erstesLaden(): Promise<void> {
    if (this.istTechnikerModus()) {
      await this.loadCustomers();
    } else {
      await this.loadProjects();
    }
    await this.search();
  }

  private async loadProjects(): Promise<void> {
    try {
      const current = await firstValueFrom(this.projectService.getActive());
      this.projekte.set((current ?? []).map((p: { id: number; name: string }) =>
        ({ id: Number(p.id), name: String(p.name ?? '') })));
    } catch {
      this.projekte.set([]);
    }
  }

  /** Projekt an- oder abwählen und die Trefferliste neu laden. */
  protected async projektUmschalten(projektId: number): Promise<void> {
    // Ab der ersten eigenen Änderung gilt die Auswahl des Lageristen, auch wenn
    // sie leer ist — sonst spränge die Server-Vorauswahl beim Abwählen zurück.
    this.projektFilterOffen = false;
    this.projektFilter.update(ids =>
      ids.includes(projektId) ? ids.filter(id => id !== projektId) : [...ids, projektId]
    );
    await this.search();
  }

  private async loadCustomers(): Promise<void> {
    try {
      const current = await firstValueFrom(this.customerService.loadCustomers());
      this.kunden.set((current ?? []).map(k => ({ id: Number(k.id), name: String(k.name ?? '') })));
    } catch {
      this.kunden.set([]);
    }
  }

  protected async search(seite = 1): Promise<void> {
    this.loading.set(true);
    this.fehler.set(null);
    try {
      this.seite.set(seite);
      const vergeben = new Set(this.pendingIds());
      const ergebnis = this.istTechnikerModus()
        ? await this.service.selectableTemplates({
            userId: this.userId() ?? undefined, query: this.searchTerm(),
            customerId: this.kundenFilter(), page: seite,
          })
        : await this.service.selectableTechnicians({
            articleTemplateId: this.articleTemplateId() ?? undefined, query: this.searchTerm(),
            projectIds: this.projektFilter(), useDefaultProjects: this.projektFilterOffen,
            comment2: this.comment2(), comment3: this.comment3(),
            page: seite,
          });

      // Beim ersten Lauf ohne eigene Auswahl die serverseitige Vorbelegung
      // übernehmen, damit sie im Filter sichtbar wird.
      if (!this.istTechnikerModus() && this.projektFilterOffen && 'applied_project_ids' in ergebnis) {
        this.projektFilter.set(ergebnis.applied_project_ids ?? []);
        this.projektFilterOffen = false;
      }

      this.uebernehmeTreffer(ergebnis, vergeben);
    } catch {
      this.fehler.set('Die Auswahl konnte nicht geladen werden.');
    } finally {
      this.loading.set(false);
    }
  }

  private uebernehmeTreffer(
    ergebnis: PaginatedResult<SelectableTemplate | SelectableTechnician>,
    vergeben: Set<number>
  ): void {
    this.matches.set(ergebnis.entries.map(entry => ({
      id: entry.id,
      label: 'description' in entry ? entry.description : entry.name,
      addressState: 'address_state' in entry ? entry.address_state : null,
      // Im Anlegen-Dialog kennt das Backend die eben erst zusammengestellten
      // Zeilen noch nicht — die kommen über pendingIds dazu.
      alreadyDefined: entry.already_defined || vergeben.has(entry.id),
    })));
    this.seitenAnzahl.set(Math.max(ergebnis.total_pages, 1));
    this.totalCount.set(ergebnis.total_count);
  }

  protected umschalten(entry: Auswahl): void {
    if (entry.alreadyDefined) return;

    const chosenIds = this.selectedIds();
    this.selected.update(current =>
      chosenIds.has(entry.id) ? current.filter(e => e.id !== entry.id) : [...current, entry]
    );
  }

  /** Schnellauswahl: alle auf dieser Seite noch wählbaren Treffer übernehmen. */
  protected alleGefilterten(): void {
    const selectable = this.selectableMatches();
    if (this.allFilteredSelected()) {
      const ids = new Set(selectable.map(e => e.id));
      this.selected.update(current => current.filter(e => !ids.has(e.id)));
      return;
    }
    const chosenIds = this.selectedIds();
    this.selected.update(current => [...current, ...selectable.filter(e => !chosenIds.has(e.id))]);
  }

  protected async goToStep2(): Promise<void> {
    if (this.selected().length === 0) return;

    this.step.set(2);
    await this.vorbelegen();
  }

  protected zurueck(): void {
    this.step.set(1);
  }

  /**
   * Belegt die Mengen mit den Durchschnittswerten anderer Techniker vor.
   *
   * Gibt es keine Vergleichswerte, bleibt 0 stehen — das ist ungültig und
   * erzwingt damit eine bewusste Eingabe, statt eine erfundene Zahl zu
   * übernehmen.
   */
  protected async vorbelegen(): Promise<void> {
    const templateIds = this.istTechnikerModus()
      ? this.selected().map(e => e.id)
      : [this.articleTemplateId() ?? 0];

    try {
      const schnitte = await this.service.averages(templateIds.filter(Boolean), this.durchschnittsLand());
      this.quantities.set(this.selected().reduce<Record<number, Mengen>>((acc, entry) => {
        const key = this.istTechnikerModus() ? entry.id : (this.articleTemplateId() ?? 0);
        const schnitt = schnitte[key];
        acc[entry.id] = { min: schnitt?.min_quantity ?? 0, target: schnitt?.target_quantity ?? 0 };
        return acc;
      }, {}));
    } catch {
      this.quantities.set(this.selected().reduce<Record<number, Mengen>>((acc, e) => {
        acc[e.id] = { min: 0, target: 0 };
        return acc;
      }, {}));
    }
  }

  /**
   * Nur ganze Stück: Die Spalten sind ganzzahlig, und eine Nachkommastelle
   * würde das Backend erst beim Speichern ablehnen — also gar nicht erst
   * entstehen lassen.
   */
  protected setzeMenge(id: number, feld: keyof Mengen, wert: string): void {
    const zahl = Number(wert);
    this.quantities.update(alle => ({
      ...alle,
      [id]: { ...(alle[id] ?? { min: 0, target: 0 }), [feld]: Number.isFinite(zahl) ? Math.round(zahl) : 0 },
    }));
  }

  /** Setzt einen Wert für alle Einträge einer Bundesland-Gruppe auf einmal. */
  protected setzeGruppe(entries: Auswahl[], feld: keyof Mengen, wert: string): void {
    const zahl = Number(wert);
    if (!Number.isFinite(zahl)) return;

    const gerundet = Math.round(zahl);
    this.quantities.update(alle => {
      const neu = { ...alle };
      for (const entry of entries) {
        neu[entry.id] = { ...(neu[entry.id] ?? { min: 0, target: 0 }), [feld]: gerundet };
      }
      return neu;
    });
  }

  /** Bundesland-Wechsel gilt abschnittsübergreifend und lädt die Vorbelegung neu. */
  protected async landGewechselt(land: string): Promise<void> {
    this.durchschnittsLand.set(land || null);
    await this.vorbelegen();
  }

  protected applyAll(): void {
    if (!this.allValid()) return;

    const werte = this.quantities();
    const selection: TechnicianStockLevelSelection[] = this.selected().map(entry => ({
      draft: {
        user_id: this.istTechnikerModus() ? (this.userId() ?? 0) : entry.id,
        article_template_id: this.istTechnikerModus() ? entry.id : (this.articleTemplateId() ?? 0),
        min_quantity: werte[entry.id].min,
        target_quantity: werte[entry.id].target,
      } satisfies TechnicianStockLevelDraft,
      label: entry.label,
      addressState: entry.addressState,
    }));

    this.dialogRef.close(selection);
  }

  protected cancel(): void {
    this.dialogRef.close(undefined);
  }
}
